import React from 'react';
import {
    Box,
    Container,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    Link,
    Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

// Assets
import logo from '../assets/bouldou white.png';

const primaryColor = '#B200BB';
const secondaryColor = '#FF3B30';

const styles = {
    root: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        display: 'flex',
        alignItems: 'center',
        py: { xs: 4, sm: 6 },
        fontFamily: '"Poppins", Arial, sans-serif',
    },
    container: {
        background: '#fff',
        borderRadius: '24px',
        boxShadow: '0 25px 70px rgba(0,0,0,0.22)',
        p: { xs: 4, sm: 6 },
        maxWidth: '800px',
    },
    logo: {
        height: '50px',
        mb: 3,
    },
    title: {
        fontWeight: 800,
        fontSize: { xs: '2.2rem', sm: '2.8rem' },
        background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textAlign: 'center',
        mb: 2,
    },
    lastRevised: {
        textAlign: 'center',
        color: '#666',
        fontStyle: 'italic',
        mb: 4,
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '1.4rem',
        color: primaryColor,
        mt: 4,
        mb: 2,
    },
    text: {
        color: '#444',
        lineHeight: 1.8,
        fontSize: '1rem',
    },
    listItem: {
        alignItems: 'flex-start',
        py: 0.8,
    },
    backButton: {
        mt: 5,
        borderRadius: '16px',
        py: 1.5,
        fontWeight: 600,
        fontSize: '1.1rem',
        background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 15px 30px rgba(178,0,187,0.4)',
        },
    },
};

const TermsAndConditions = () => {
    const navigate = useNavigate();

    return (
        <Box sx={styles.root}>
            <Container maxWidth="md">
                <Box sx={styles.container}>
                    {/* Logo */}
                    <Box textAlign="center">
                        <img src={logo} alt="Bouldou" style={styles.logo} />
                    </Box>

                    <Typography variant="h1" sx={styles.title}>
                        Terms and Conditions
                    </Typography>
                    <Typography variant="body2" sx={styles.lastRevised}>
                        Last revised on August 30, 2025
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    {/* 1. Acceptance */}
                    <Typography sx={styles.sectionTitle}>1. Acceptance of Terms of Use Agreement</Typography>
                    <Typography sx={styles.text} paragraph>
                        Welcome to <strong>Bouldou</strong>. If you are Seychellois, these Terms of Use are between you and{' '}
                        <strong>Bouldou.sc Pty Ltd</strong> located at Anse Etoile, Mahé, Seychelles. You can contact us at{' '}
                        <Link href="mailto:contactbouldou@gmail.com" color={primaryColor} underline="hover">
                            contactbouldou@gmail.com
                        </Link>.
                    </Typography>
                    <Typography sx={styles.text} paragraph>
                        By creating a Bouldou account through a mobile device, application, or computer (collectively, the "Service"), you agree to be bound by:
                    </Typography>
                    <List>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• These Terms of Use" />
                        </ListItem>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• The Privacy Policy, incorporated by reference" />
                        </ListItem>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• Any terms for additional features, products, or services you purchase" />
                        </ListItem>
                    </List>
                    <Typography sx={styles.text} paragraph>
                        If you do not agree to all terms, do not use the Service.
                    </Typography>
                    <Typography sx={styles.text} paragraph>
                        We may update this Agreement. Material changes will be notified in advance via the Service or email. Continued use after changes means you accept the revised Agreement.
                    </Typography>

                    {/* 2. Eligibility */}
                    <Typography sx={styles.sectionTitle}>2. Eligibility</Typography>
                    <Typography sx={styles.text} paragraph>
                        You must be at least <strong>18 years old</strong> to use the Service. By using it, you represent that:
                    </Typography>
                    <List>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• You can form a binding contract with Bouldou" />
                        </ListItem>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• You are not barred from using the Service under Seychellois or applicable laws" />
                        </ListItem>
                        <ListItem sx={styles.listItem}>
                            <ListItemText primary="• You have never been convicted of a felony, sex crime, or violent crime, and are not required to register as a sex offender" />
                        </ListItem>
                    </List>

                    {/* 3. Your Account */}
                    <Typography sx={styles.sectionTitle}>3. Your Account</Typography>
                    <Typography sx={styles.text} paragraph>
                        You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. Notify us immediately of any unauthorized use.
                    </Typography>

                    {/* 4. Modifying the Service and Termination */}
                    <Typography sx={styles.sectionTitle}>4. Modifying the Service and Termination</Typography>
                    <Typography sx={styles.text} paragraph>
                        We may modify, suspend, or terminate the Service at any time. You may delete your account in Settings. We may terminate your account for violations without refund.
                    </Typography>

                    {/* 5. Safety */}
                    <Typography sx={styles.sectionTitle}>5. Safety & Interactions</Typography>
                    <Typography sx={styles.text} paragraph>
                        We promote respect but are not responsible for member conduct. Exercise caution in all interactions, especially in person. We do not perform background checks.
                    </Typography>

                    {/* 6. Rights You Grant */}
                    <Typography sx={styles.sectionTitle}>6. Rights You Grant Bouldou</Typography>
                    <Typography sx={styles.text} paragraph>
                        You grant Bouldou a worldwide, royalty-free, transferable license to use, store, display, reproduce, modify, and distribute your Content to operate and improve the Service.
                    </Typography>

                    {/* 7. Community Rules */}
                    <Typography sx={styles.sectionTitle}>7. Community Rules</Typography>
                    <Typography sx={styles.text} paragraph>
                        You agree not to:
                    </Typography>
                    <List>
                        <ListItem sx={styles.listItem}><ListItemText primary="• Use the Service illegally or harmfully" /></ListItem>
                        <ListItem sx={styles.listItem}><ListItemText primary="• Harass, bully, defame, or impersonate others" /></ListItem>
                        <ListItem sx={styles.listItem}><ListItemText primary="• Post hate speech, nudity, violence, or infringe rights" /></ListItem>
                        <ListItem sx={styles.listItem}><ListItemText primary="• Spam, solicit money, or create multiple accounts" /></ListItem>
                    </List>
                    <Typography sx={styles.text} paragraph>
                        Violations may result in immediate account termination without refund.
                    </Typography>

                    {/* 8. Other Members' Content */}
                    <Typography sx={styles.sectionTitle}>8. Other Members' Content</Typography>
                    <Typography sx={styles.text} paragraph>
                        Content posted by members is their responsibility. Report violations in-app or via email.
                    </Typography>

                    {/* 9. Purchases */}
                    <Typography sx={styles.sectionTitle}>9. Purchases</Typography>
                    <Typography sx={styles.text} paragraph>
                        All in-app purchases (virtual items, subscriptions) are final and non-refundable.
                    </Typography>

                    {/* 11. Copyright Claims */}
                    <Typography sx={styles.sectionTitle}>11. Copyright Infringement</Typography>
                    <Typography sx={styles.text} paragraph>
                        To report copyright infringement, send a notice to contactbouldou@gmail.com including all required details under applicable law.
                    </Typography>

                    {/* 12. Disclaimers */}
                    <Typography sx={styles.sectionTitle}>12. Disclaimers</Typography>
                    <Typography sx={styles.text} paragraph>
                        The Service is provided "as is" with no warranties. We do not guarantee uninterrupted, secure, or error-free operation.
                    </Typography>

                    {/* 14. Limitation of Liability */}
                    <Typography sx={styles.sectionTitle}>14. Limitation of Liability</Typography>
                    <Typography sx={styles.text} paragraph>
                        To the fullest extent permitted by law, Bouldou is not liable for indirect, incidental, or consequential damages arising from use of the Service.
                    </Typography>

                    {/* Summary */}
                    <Typography sx={styles.sectionTitle}>Summary</Typography>
                    <Typography sx={styles.text} paragraph>
                        By using Bouldou, you agree to follow our rules, respect others, and understand that we provide the Service "as is". We may update these terms — continued use means acceptance.
                    </Typography>

                    <Box sx={{ mt: 6, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              fullWidth
              sx={{
                py: 1.8,
                borderRadius: '16px',
                fontWeight: 700,
                fontSize: '1.1rem',
                border: `2.5px solid ${primaryColor}`,
                color: primaryColor,
                '&:hover': {
                  borderColor: primaryColor,
                  bgcolor: `${primaryColor}10`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Back to Home
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              fullWidth
              sx={{
                py: 1.8,
                borderRadius: '16px',
                fontWeight: 700,
                fontSize: '1.1rem',
                background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
                boxShadow: '0 10px 30px rgba(178, 0, 187, 0.4)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 35px rgba(178, 0, 187, 0.5)',
                },
              }}
            >
              Create Account
            </Button>
          </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default TermsAndConditions;